import type { RefdataFeature } from '../../types/refdata';

import type { PadusUnavailableSourceUnit } from '../../types/siteEvidence';

type Qualification = { sourceOid: number; nativeGeometryReceiptSha256: string; sourceUnitQualificationSha256: string;
  rawSourcePin: NonNullable<PadusUnavailableSourceUnit['rawSourcePin']>;
  unavailableReason?: string; uncertaintyEnvelope?: { nativeBounds?: number[]; geometryRepaired?: boolean; originalProtectedGeometryRecovered?: boolean } };

const HASH = /^[a-f0-9]{64}$/;
function sourceQualification(feature: RefdataFeature): Qualification | null {
  const text = feature.props.padusSourceQualification;
  if (typeof text !== 'string' || text.length > 64 * 1024) return null;
  try {
    const q = JSON.parse(text) as Qualification;
    const id = feature.props.sourceFeatureId;
    const match = typeof id === 'string' ? /^padus:([a-f0-9]{64}):([1-9]\d*)$/.exec(id) : null;
    if (!match || !Number.isSafeInteger(q.sourceOid) || q.sourceOid !== feature.props.OBJECTID || String(q.sourceOid) !== match[2]
      || !feature.id.startsWith(`${id}:part:`) || !HASH.test(q.nativeGeometryReceiptSha256) || !HASH.test(q.sourceUnitQualificationSha256)
      || !q.rawSourcePin || typeof q.rawSourcePin.path !== 'string'
      || !new RegExp(`^refdata/padus/2026-09-08-native-source-archive/raw/padus/${match[1]}/pages/[a-f0-9]{64}\\.json$`).test(q.rawSourcePin.path)
      || typeof q.rawSourcePin.generation !== 'string' || !/^[1-9]\d*$/.test(q.rawSourcePin.generation)
      || !Number.isSafeInteger(q.rawSourcePin.bytes) || q.rawSourcePin.bytes <= 0 || !HASH.test(q.rawSourcePin.sha256)) return null;
    return q;
  } catch { return null; }
}

/** Complements and uncertainty masks never count as observed protected units. */
export function classifyPadusFeature(feature: RefdataFeature):
  | { kind: 'complement' }
  | { kind: 'observed'; sourceFeatureId: string }
  | { kind: 'unavailable'; unit: PadusUnavailableSourceUnit } {
  if (feature.props.Unit_Nm === 'Non-PAD-US Area' || feature.props.NAME === 'Non-PAD-US Area') return { kind: 'complement' };
  const kind = feature.props.padusContextKind;
  if (kind === undefined || kind === null) return { kind: 'observed', sourceFeatureId: feature.id };
  const q = sourceQualification(feature);
  const id = typeof feature.props.sourceFeatureId === 'string' ? feature.props.sourceFeatureId : feature.id;
  if (kind === 'observed-inventory-unit' && q && feature.props.negativeFindingQualified === 'false') return { kind: 'observed', sourceFeatureId: id };
  const e = q?.uncertaintyEnvelope;
  const bounds = e?.nativeBounds;
  const qualifiedEnvelope = kind === 'unavailable-source-envelope' && q && typeof q.unavailableReason === 'string'
    && q.unavailableReason.length > 0 && e?.geometryRepaired === false && e.originalProtectedGeometryRecovered === false
    && Array.isArray(bounds) && bounds.length === 4 && bounds.every(Number.isFinite) && bounds[0]! < bounds[2]! && bounds[1]! < bounds[3]!;
  return { kind: 'unavailable', unit: {
    sourceFeatureId: id, name: typeof feature.props.Unit_Nm === 'string' ? feature.props.Unit_Nm : 'Unnamed PAD-US source unit',
    reason: qualifiedEnvelope ? `Original source geometry unavailable: ${q.unavailableReason}. The returned footprint is its conservative source envelope, not observed protected land.`
      : 'Native PAD-US feature qualification is unavailable or inconsistent; this feature cannot establish protected-unit overlap or absence.',
    ...(qualifiedEnvelope ? { nativeBounds: bounds, rawSourcePin: q.rawSourcePin, sourceQualificationReceiptHash: q.sourceUnitQualificationSha256 } : {}),
    parcelIds: [], intersectsSiteScope: false,
  } };
}
